import { useState } from 'react';
import { CalendarClock, ChevronLeft, FileUp, Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { RecurringTemplate } from '../../domain/model';
import { Button, ButtonLink, CategoryDot, EmptyState, IconButton, List, ListRow, LoadingState, ScreenTitle, Section, series, useFormat } from '../../ui';
import { BillList, openFixed, openRows, useBills, useRecordBill } from './bills';
import { TemplateSheet } from './TemplateSheet';
import { ActiveBudgetReadOnly } from '../../premium/ReadOnlyNotice';
import { YearOfBills } from './YearOfBills';

/**
 * More → Recurring expenses: this month's bills in due order. Fixed ones
 * are recorded with one tap (or all at once), variable ones show last
 * month's amount next to "Enter amount".
 */
export function RecurringScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { money } = useFormat();
  const data = useBills();
  const recorder = useRecordBill(data);
  const [editing, setEditing] = useState<RecurringTemplate | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [recordingAll, setRecordingAll] = useState(false);

  const back = (
    <div className="screen-back">
      <IconButton icon={ChevronLeft} label={t('catalog.backToMore')} onClick={() => navigate('/more')} />
    </div>
  );
  if (!data) {
    return (
      <>
        {back}
        <LoadingState rows={5} />
      </>
    );
  }

  const edit = (tpl: RecurringTemplate | null) => {
    setEditing(tpl);
    setSheetOpen(true);
  };
  const paused = data.templates.filter((x) => !x.active);
  const open = openRows(data.rows);
  const fixed = openFixed(data.rows);
  const fixedTotal = fixed.reduce((s, r) => s + r.suggestedMinor!, 0);
  const dueTotal = open.reduce((s, r) => s + (r.suggestedMinor ?? 0), 0);
  const done = data.rows.length - open.length;

  return (
    <>
      {back}
      <ScreenTitle>{t('recurring.title')}</ScreenTitle>
      <ActiveBudgetReadOnly />

      {data.templates.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title={t('recurring.emptyTitle')}
          body={t('recurring.emptyBody')}
          action={
            <div className="screen-actions">
              <Button variant="primary" size="lg" icon={Plus} onClick={() => edit(null)}>
                {t('recurring.new')}
              </Button>
              <ButtonLink to="/import" variant="ghost" size="lg" icon={FileUp}>
                {t('import.fromBills')}
              </ButtonLink>
            </div>
          }
        />
      ) : (
        <>
          {data.rows.length > 0 && (
            <p className="bills-summary" data-testid="bills-summary">
              <span>{t('recurring.summary', { done, total: data.rows.length })}</span>
              {dueTotal > 0 && <span className="tone-muted">{t('recurring.stillDue', { amount: money(dueTotal) })}</span>}
            </p>
          )}
          {fixed.length > 1 && (
            <div className="screen-actions bills-record-all">
              <Button
                variant="primary"
                size="lg"
                block
                loading={recordingAll}
                onClick={() => {
                  setRecordingAll(true);
                  void recorder.recordAll(fixed).finally(() => setRecordingAll(false));
                }}
              >
                {t('recurring.recordAll', {
                  count: fixed.length,
                  amount: money(fixedTotal),
                })}
              </Button>
            </div>
          )}
          {data.rows.length > 0 && <BillList data={data} rows={data.rows} recorder={recorder} onEdit={edit} />}
          <div className="screen-actions">
            <Button variant="secondary" size="lg" block icon={Plus} onClick={() => edit(null)}>
              {t('recurring.new')}
            </Button>
            <ButtonLink to="/import" variant="ghost" size="lg" block icon={FileUp}>
              {t('import.fromBills')}
            </ButtonLink>
          </div>
        </>
      )}

      <YearOfBills data={data} onEdit={(id) => edit(data.templates.find((x) => x.id === id) ?? null)} />

      {paused.length > 0 && (
        <Section title={t('recurring.pausedSection')}>
          <p className="tone-muted section-note">{t('recurring.pausedBody')}</p>
          <List>
            {paused.map((p) => (
              <ListRow
                key={p.id}
                leading={<CategoryDot color={series(data.categories.get(p.categoryId)?.colorSlot ?? 0)} />}
                title={p.name}
                subtitle={p.kind === 'fixed' ? t('recurring.fixed') : t('recurring.variable')}
                onClick={() => edit(p)}
              />
            ))}
          </List>
        </Section>
      )}

      {recorder.sheet}
      <TemplateSheet open={sheetOpen} template={editing} data={data} onClose={() => setSheetOpen(false)} />
    </>
  );
}

/**
 * Home's "Bills due": the open bills of this period with their one-tap
 * actions. Renders nothing when every bill is recorded (or none exist).
 */
export function DueBills() {
  const { t } = useTranslation();
  const data = useBills();
  const recorder = useRecordBill(data);
  if (!data) return null;
  const open = openRows(data.rows);
  if (open.length === 0) return recorder.sheet;
  return (
    <Section
      title={t('recurring.homeTitle')}
      action={
        <Link className="link-button" to="/more/recurring">
          {t('recurring.seeAll')}
        </Link>
      }
    >
      <BillList data={data} rows={open.slice(0, 4)} recorder={recorder} />
      {recorder.sheet}
    </Section>
  );
}
